import React from "react";
import styled from "styled-components";
import DefaultLayout from "../../layouts/DefaultLayout";
import TemplateColumn from "../../layouts/TemplateColumn";
import {
  PageHeader,
  PageSection,
  RelatedContent,
  PatientSupportCard,
  PatientMessageCard,
  SupportIconList,
} from "../../components";
import { jobCodes } from "../../util/data/jobCodes";
import MerckSupportLogo from "../../assets/merck-support-logo-desk.svg";
import MerckSupportMobLogo from "../../assets/merck-support-logo-mob.svg";
import * as Text from "../../util/typography/Text"

// SEO INFORMATION
const pageMeta = {
    title: `Patient Support Program for KEYTRUDA® (pembrolizumab) | HCP`,
    keywords: `patient support program for keytruda`,
    description: `Health care professionals may share information with their eligible patients about the KEY+YOU Patient Support Program for KEYTRUDA® (pembrolizumab).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/key-you-patient-support-program/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/key-you-patient-support-program/","@type":"MedicalWebPage","name":"The KEY+YOU Patient Support Program for KEYTRUDA® (pembrolizumab)","description":"Patients and caregivers can get additional support throughout treatment by visiting keytruda.com or calling 855⁠-⁠398⁠-⁠7832 and pressing 2 to speak with a representative. Support includes educational resources and information to connect you with organizations that may help address day-to-day needs. The KEY+YOU Patient Support Program does not take the place of talking to your doctor or health care team. If you have any questions that are specific to your medical condition or treatment, or you’re having side effects, please contact your doctor’s office right away. This program provides patients access to educational resources to support them throughout their treatment journey."}`
    ]
}

const jobCode = jobCodes[31].jobCode;

const relatedContent = [
  {
    section: "Resources",
    button:{
      theme:"dark",
      isExternal:false,
    },
    link: {
      url: "/resources/hcp-and-patient-resources/",
      label: "Resource Center",
    },
  },
  {
    section: "Resources",
    button:{
      theme:"dark",
      isExternal:true,
    },
    link: {
      url: "https://www.keytruda.com/patient-and-caregiver-resources/?utm_medium=referral&utm_source=mymercksupport.com&utm_term=Vanity%20URL",
      label: "Patient Site",
    },
  },
];

const resourceData = [
  {
    iconColumn: {
      icon: '/static/images/psp/image1.svg',
      alt: ''
    },
    contentMarkup: `A dedicated team member who can address nonmedical concerns and provide one-on-one phone support`,
  },
  {
    iconColumn: {
      icon: '/static/images/psp/image2.svg',
      alt: ''
    },
    contentMarkup: `A starter kit that includes a treatment journal to help patients track and report their symptoms and a cookbook for meal planning and recipes`,
  },
  {
    iconColumn: {
      icon: '/static/images/psp/image3.svg',
      alt: ''
    },
    contentMarkup: `Educational resources and practical tips that may help them feel prepared during treatment`,
  },
  {
    iconColumn: {
      icon: '/static/images/psp/image4.svg',
      alt: ''
    },
    contentMarkup: `Connection to local services to help manage transportation, childcare, and more`,
  },
]

const PageWrapper = styled.div`
    .support-card-section {
      padding-top:20px;
    }
      
    .support-icon-section {
          h2.marginBottom {
              margin-bottom: 0;
          }

          h2 {
              color: #262626;
              font-size: 30px;
              font-weight: 400;
              line-height: 38px;

              @media (max-width: 768px) {
                font-size: 22px;
                line-height: 28px;
              }
          }
      }
    .support-msg-section {
      padding-bottom:60px;

      @media (max-width: 768px) {
       padding-bottom:40px;
      }

    }
    
    .support-message-card {
      margin-bottom: 20px !important;
    }
`;

const Page = ({ location }) => {
  return (
    <PageWrapper>
      <DefaultLayout pageMeta={pageMeta} location={location} jobCode={jobCode}>
        <div data-template-name="template-c">
          {/* Begin Intro Column */}
          <TemplateColumn id="template-c-intro">
            {/* <PageHeader title="Support for Patients" /> */}
            <PageHeader batch2 brand="qlex" title="Support for Patients" />
            <PageSection customClasses="support-card-section">
              <PatientSupportCard
                desktopImage={MerckSupportLogo}
                mobileImage={MerckSupportMobLogo}
                imageAlt="Merck Patient Support"
                heading="Supporting Patients Along Their Prescribed Treatment Plan"
                description={
                  <>
                    Once treatment begins, a patient may feel unprepared. My Merck Support is designed to provide patients who are prescribed KEYTRUDA or KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL with educational resources, practical tips, and helpful tools during treatment.
                  </>
                }
              />
            </PageSection>
            <PageSection
              title="Once Enrolled, Your Patients Get Tailored Support, Including:"
              customClasses="support-icon-section"
              maxWidth={1032}
            >
              <SupportIconList data={resourceData} />
            </PageSection>
            <PageSection customClasses="support-msg-section">
              <PatientMessageCard className="support-message-card"
              heading="Encourage Your Patients to Enroll for Support"
                message={
                  <>
                  Patients who are prescribed KEYTRUDA or KEYTRUDA QLEX can learn more and enroll by calling <span className="text-nowrap">888-55-MyMRK</span> <a className="text-nowrap" href="tel:8885569675">(888-556-9675)</a>, Mon–Fri, 8am–8pm ET, or by visiting <a target="_blank" href="https://MyMerckSupport.com/?utm_source=KeytrudaHCP&utm_medium=referral">
                      MyMerckSupport.com
                    </a>.
                  </>
                }
              />
            <Text.Footnote>
               My Merck Support is brought to you by Merck. This program is not a substitute for professional or medical advice. Patients should contact their health care provider’s office with questions about their medical condition, side effects, and treatment.
            </Text.Footnote>
            </PageSection>
          </TemplateColumn>
          {/* End Intro Column */}

          <TemplateColumn id="template-e-secondary">
            <PageSection bgColor="keytrudaGreen">
              <RelatedContent items={relatedContent}/>
            </PageSection>
          </TemplateColumn>
        </div>
      </DefaultLayout>
    </PageWrapper>
  );
};

export default Page;
